import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import Cookies from "js-cookie";
import { useSelector, useDispatch } from "react-redux";
import { RefreshCw, MoreHorizontal, ChevronDown, ChevronRight } from "lucide-react";
import Table from "../../../components/ACS-Dashboard/ACS-Blocks/Table";
import GlobalPagination from "../../../components/ACS-Dashboard/ACS-Blocks/GlobalPagination";
import ActionMenu from "../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import SearchBox from "../../../components/ACS-Dashboard/ACS-Blocks/SearchBox";
import FilterButton from "../../../components/ACS-Dashboard/ACS-Blocks/FilterButton";
import Loader from "../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import useExpandedRowHover from "../../../components/ACS-Dashboard/ACS-Blocks/useExpandedRowHover";
import Tabs from "../../../components/ACS-Dashboard/ACS-Blocks/Tabs";
import notify from "../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import TransactionDropdownContent from "./TransactionDropdownContent";
import { selectModuleTabs } from "../../../redux/selectors/tabsSelectors";
import { addTab, removeTab, setActiveTab } from "../../../redux/slices/transactionAuditSlice";
import {
    useTableSavedViews,
    TableSavedViewsControl,
    TABLE_PAGE_KEYS,
} from "../../TableSavedViews";
import {
    DEFAULT_DRAFT_FILTERS,
    DEFAULT_FILTER_LABELS,
    DEFAULT_FILTER_VALUES,
    FILTER_OPTIONS,
    FLOW_ROW_LABELS,
    SOURCE_ROW_LABELS,
    STATUS_ROW_LABELS,
    deriveFilterLabels,
    formatEnumValue,
    toApiFilterValues,
} from "./transactionFilters";

// ─── Constants ────────────────────────────────────────────────────────────────

// Ref. Type / Reference Number sit at the extreme left (right after the fixed
// Selection/Action columns), per LGX-941 — both added to defaultStickyColumnKeys
// below so the frozen-left group stays a contiguous prefix.
const TABLE_HEADERS = [
    "Selection",
    "Action",
    "Ref. Type",
    "Reference Number",
    "Transaction No.",
    "File Type",
    "Flow Type",
    "File Name",
    "Shipper",
    "Carrier",
    "Source",
    "Processed",
    "Date/Time",
];

const SORTABLE_COLUMNS = {
    "Transaction No.":  "transactionNumber",
    "File Type":        "fileType",
    "Flow Type":        "flowType",
    "Ref. Type":        "referenceType",
    "Reference Number": "referenceNumber",
    "File Name":        "fileName",
    "Processed":        "processingStatus",
    "Date/Time":        "dateTimeProcessed",
    "Source":           "source",
    // Shipper/Carrier are resolved via a joined Shipment lookup, not a plain audit-row
    // column, so they're intentionally not sortable here.
};

// ─── Layout constants (aligned with ShipmentsTable) ───────────────────────────

const DATA_COLUMN_MIN_WIDTH     = 200;
const DATA_COLUMN_MAX_WIDTH     = 420;
const SELECTION_COLUMN_WIDTH    = 80;
const ACTION_COLUMN_WIDTH       = 50;
const TRANSACTION_NUMBER_HEADER = "Transaction No.";

// Badge shell shared by Flow Type / Ref. Type / Source / Processed, matching
// the shipment status badge (see statusBadgeClass in shipmentShared).
const BADGE_CLASS = "text-fluid-sm px-fluid-1.5 py-fluid-1 rounded font-medium";

// Neutral shell for a value none of the maps below recognise, so an unfamiliar enum
// reads as "unknown" instead of borrowing the colour of a known one.
const UNKNOWN_BADGE_CLASS = "bg-gray-100 dark:bg-gray-700/40 text-black dark:text-gray-200";

const flowBadgeClass = {
    INBOUND:  "bg-[#223688]/10 text-[#223688] dark:bg-[#223688]/30 dark:text-[#6DE1C2]",
    OUTBOUND: "bg-[#6DE1C2]/20 text-[#085041] dark:bg-[#6DE1C2]/20 dark:text-[#6DE1C2]",
    DEFAULT:  UNKNOWN_BADGE_CLASS,
};

const sourceBadgeClass = {
    IFS:         "bg-blue-100  dark:bg-blue-900/40  text-black dark:text-blue-200",
    CARRIER_EDI: "bg-amber-100 dark:bg-amber-900/40 text-black dark:text-amber-200",
    ASCEND:      "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
    DEFAULT:     UNKNOWN_BADGE_CLASS,
};

const processingStatusBadgeClass = {
    SUCCESSFUL:   "bg-green-100 dark:bg-green-900/40 text-black dark:text-green-200",
    UNSUCCESSFUL: "bg-red-100   dark:bg-red-900/40   text-black dark:text-red-200",
    // Not red — an unrecognised status is not the same claim as a failed one.
    DEFAULT:      UNKNOWN_BADGE_CLASS,
};

// ─── Utils ────────────────────────────────────────────────────────────────────

const formatFileType = (val) => {
    if (!val) return "—";
    return val
        .replace(/^EDI_/, "")
        .replace("_INBOUND", "_IN")
        .replace("_OUTBOUND", "_OUT");
};

const formatDateTime = (val) => (val ? new Date(val).toLocaleString() : "—");

/**
 * Formats a raw API transaction into a display-ready table row map.
 *
 * Keyed by header (like formatShipmentRow) so the body can render whatever
 * column order / visibility the active saved view resolves to. `onOpenDocument`
 * is only invoked when the row actually has a document behind it (`hasDocument`)
 * — otherwise File Name renders as plain text, same as before LGX-941.
 */
const formatTransactionRow = (transaction, onOpenDocument) => ({
    _id: transaction?.id,
    [TRANSACTION_NUMBER_HEADER]: transaction?.transactionNumber,
    "File Type": formatFileType(transaction?.fileType),
    "Flow Type": (
        <span className={`${BADGE_CLASS} ${
            flowBadgeClass[transaction?.flowType] ?? flowBadgeClass.DEFAULT
        }`}>
            {formatEnumValue(transaction?.flowType, FLOW_ROW_LABELS)}
        </span>
    ),
    "Ref. Type": transaction?.referenceType && (
        <span className={`${BADGE_CLASS} bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300`}>
            {transaction.referenceType}
        </span>
    ),
    "Reference Number": transaction?.referenceNumber || "—",
    "File Name": transaction?.hasDocument ? (
        <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onOpenDocument?.(transaction.id); }}
            className="text-[#223688] dark:text-[#6DE1C2] hover:underline text-left truncate max-w-full"
            title="Open document"
        >
            {transaction?.fileName || "—"}
        </button>
    ) : (
        transaction?.fileName || "—"
    ),
    "Shipper": transaction?.shipperName || "—",
    "Carrier": transaction?.carrierName || "—",
    "Source": (
        <span className={`${BADGE_CLASS} ${
            sourceBadgeClass[transaction?.source] ?? sourceBadgeClass.DEFAULT
        }`}>
            {formatEnumValue(transaction?.source, SOURCE_ROW_LABELS)}
        </span>
    ),
    "Processed": (
        <span className={`${BADGE_CLASS} ${
            processingStatusBadgeClass[transaction?.processingStatus] ?? processingStatusBadgeClass.DEFAULT
        }`}>
            {formatEnumValue(transaction?.processingStatus, STATUS_ROW_LABELS)}
        </span>
    ),
    "Date/Time": formatDateTime(transaction?.dateTimeProcessed),
});

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * One tab's worth of Transaction Audit UI — the "Transactions" default tab and
 * every "Search: …" tab opened from its search box are each an independent
 * instance of this component (mounted fresh per tab, like Shipment's search tabs).
 *
 * A search tab gets its own `stateKey` (its tab id) instead of the shared
 * "transaction-audit" pageKey, so it never picks up a saved view — real, named
 * saved views only ever exist under the shared pageKey, so a fresh per-tab key
 * always resolves to "no view", letting `initialConfig` seed the keyword below
 * instead of that resolution silently clearing it back to "".
 */
const TransactionAuditDetails = ({
    initialKeyword = "",
    onSearchNewTab,
    stateKey = TABLE_PAGE_KEYS.TRANSACTION_AUDIT,
    toolbarSlot = null,
}) => {
    const [data, setData]                         = useState([]);
    const [totalElements, setTotalElements]       = useState(0);
    const [totalPages, setTotalPages]             = useState(1);
    const [loading, setLoading]                   = useState(false);
    const [reloading, setReloading]               = useState(false);
    const [error, setError]                       = useState(null);

    const [searchQuery, setSearchQuery]           = useState(initialKeyword);
    const [debouncedKeyword, setDebouncedKeyword] = useState(initialKeyword);

    const [fileType, setFileType]                 = useState(DEFAULT_FILTER_VALUES.fileType);
    const [flowType, setFlowType]                 = useState(DEFAULT_FILTER_VALUES.flowType);
    const [referenceType, setReferenceType]       = useState(DEFAULT_FILTER_VALUES.referenceType);
    const [processingStatus, setProcessingStatus] = useState(DEFAULT_FILTER_VALUES.processingStatus);
    const [source, setSource]                     = useState(DEFAULT_FILTER_VALUES.source);

    const [fileTypeLabel, setFileTypeLabel]       = useState(DEFAULT_FILTER_LABELS.fileTypeLabel);
    const [flowTypeLabel, setFlowTypeLabel]       = useState(DEFAULT_FILTER_LABELS.flowTypeLabel);
    const [refTypeLabel, setRefTypeLabel]         = useState(DEFAULT_FILTER_LABELS.refTypeLabel);
    const [statusLabel, setStatusLabel]           = useState(DEFAULT_FILTER_LABELS.statusLabel);
    const [sourceLabel, setSourceLabel]           = useState(DEFAULT_FILTER_LABELS.sourceLabel);

    const [draftFilters, setDraftFilters]         = useState(DEFAULT_DRAFT_FILTERS);

    const [currentPage, setCurrentPage]           = useState(1);
    const [itemsPerPage, setItemsPerPage]         = useState(50);

    const [sortBy, setSortBy]                     = useState("dateTimeProcessed");
    const [ascending, setAscending]               = useState(false);

    const [openMenu, setOpenMenu]                 = useState(null);
    const [dropdownPos, setDropdownPos]           = useState({ top: 0, left: 0 });
    const [showLoadingOverlay, setShowLoadingOverlay] = useState(false);

    // Hovering any cell wraps the whole row, so long values in every column are readable at once.
    const { expandedRowKey, getRowHoverProps }    = useExpandedRowHover();

    const dropdownRef                             = useRef(null);
    const checkboxHeaderRef                       = useRef(null);
    const tableScrollRef                          = useRef(null);

    /**
     * Applies a whole filter set at once (used when a saved view is applied / reset).
     * Labels are derived from the values rather than passed in, so the dropdown can
     * never disagree with what the request actually filters on.
     */
    const applyFilterValues = useCallback((values = {}) => {
        const next = {
            fileType:         values.fileType         ?? DEFAULT_FILTER_VALUES.fileType,
            flowType:         values.flowType         ?? DEFAULT_FILTER_VALUES.flowType,
            referenceType:    values.referenceType    ?? DEFAULT_FILTER_VALUES.referenceType,
            processingStatus: values.processingStatus ?? DEFAULT_FILTER_VALUES.processingStatus,
            source:           values.source           ?? DEFAULT_FILTER_VALUES.source,
        };
        const labels = deriveFilterLabels(next);

        setFileType(next.fileType);
        setFlowType(next.flowType);
        setReferenceType(next.referenceType);
        setProcessingStatus(next.processingStatus);
        setSource(next.source);
        setFileTypeLabel(labels.fileTypeLabel);
        setFlowTypeLabel(labels.flowTypeLabel);
        setRefTypeLabel(labels.refTypeLabel);
        setStatusLabel(labels.statusLabel);
        setSourceLabel(labels.sourceLabel);
    }, []);

    /**
     * Commits a keyword as an applied filter (view restore, Apply Filters, Reset).
     * The top-bar search box never calls this — it opens a new tab instead (see
     * handleSearch) — so typing there can't silently filter this tab's own data.
     */
    const applyKeyword = useCallback((value = "") => {
        setSearchQuery(value);
        setDebouncedKeyword(value);
    }, []);

    // Per-user column layout + sort/filter presets, same hook the shipment table uses.
    const {
        columnWidths: savedColumnWidths,
        setColumnWidths: onColumnResize,
        dataHeaders,
        controlLabel,
        views,
        activeView,
        loading: viewsLoading,
        error: viewsError,
        isDirty,
        selectView,
        saveNewView,
        updateActiveView,
        renameView,
        deleteView,
        setDefaultView,
        updateColumnVisibility,
        markDirty,
        // Views-driven column manager (full column set, hidden columns surface in the
        // "Hidden" section). Spread `columnManagerProps` onto <Table>; the rich panel
        // renders inside the single Views dropdown via `onManageColumns`.
        columnManagerProps,
        onManageColumns,
    } = useTableSavedViews({
        pageKey: stateKey,
        allHeaders: TABLE_HEADERS,
        fixedPrefixCount: 2,
        defaultStickyColumnKeys: ["Selection", "Action", "Ref. Type", "Reference Number", TRANSACTION_NUMBER_HEADER],
        // A search tab's pageKey never has a saved view, so "no view selected" falls
        // back to this instead of a bare empty config — the keyword it was opened
        // with. The main "Transactions" tab has no baseline of its own here (real
        // saved views live under its pageKey and take priority over this anyway).
        initialConfig: initialKeyword ? { keyword: initialKeyword } : null,
        getExtraConfig: () => ({
            sortBy,
            ascending,
            currentPage,
            pageSize: itemsPerPage,
            // Only the API values are stored; labels are derived on the way back in.
            filters: { fileType, flowType, referenceType, processingStatus, source },
            // The dropdown treats the keyword as part of the filter set, so a view
            // has to round-trip it too.
            keyword: debouncedKeyword,
        }),
        onApplyConfig: (config, viewMeta, options = {}) => {
            if (config.sortBy    !== undefined) setSortBy(config.sortBy);
            if (config.ascending !== undefined) setAscending(config.ascending);
            if (config.currentPage) setCurrentPage(config.currentPage);
            if (config.pageSize)    setItemsPerPage(config.pageSize);

            // Column-manager edits re-apply a layout-only config (no view attached,
            // no reset flag) and must leave the active filters alone. A view switch,
            // Reset, or initial load owns the entire filter set instead: whatever the
            // config omits goes back to default, so filters from the previously
            // active view can't leak into the new one.
            const ownsFilterSet =
                Boolean(viewMeta) || options.resetExtraConfig || options.isInitialLoad;
            if (!ownsFilterSet) return;

            applyFilterValues(config.filters ?? DEFAULT_FILTER_VALUES);
            applyKeyword(config.keyword ?? "");
        },
    });

    // Re-seed the dropdown whenever applied state changes elsewhere — the top-bar
    // search box, a saved view being applied, or Reset.
    useEffect(() => {
        setDraftFilters({
            fileTypeLabel,
            flowTypeLabel,
            refTypeLabel,
            statusLabel,
            sourceLabel,
            keyword: searchQuery,
        });
    }, [fileTypeLabel, flowTypeLabel, refTypeLabel, statusLabel, sourceLabel, searchQuery]);

    // ── Loading overlay: only show after 250ms to avoid flash on fast requests ──
    useEffect(() => {
        if (!loading) { setShowLoadingOverlay(false); return; }
        const t = setTimeout(() => setShowLoadingOverlay(true), 250);
        return () => clearTimeout(t);
    }, [loading]);

    useEffect(() => {
        const handle = (e) => {
            if (e.target.closest("[data-action-trigger]")) return;
            if (dropdownRef.current && !dropdownRef.current.contains(e.target))
                setOpenMenu(null);
        };
        document.addEventListener("mousedown", handle);
        return () => document.removeEventListener("mousedown", handle);
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const params = {
                page: currentPage - 1,
                size: itemsPerPage,
                sort: `${sortBy},${ascending ? "asc" : "desc"}`,
                ...(debouncedKeyword  && { keyword: debouncedKeyword }),
                ...(fileType          && { fileType }),
                ...(flowType          && { flowType }),
                ...(referenceType     && { referenceType }),
                ...(processingStatus  && { processingStatus }),
                ...(source            && { source }),
            };
            const res = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/transaction-audit`,
                {
                    params,
                    headers: {
                        Authorization: `Bearer ${Cookies.get("authToken")}`,
                        // Every office the user belongs to, not just the first one. The
                        // endpoint takes a comma-separated list and scopes results to it
                        // (TransactionAuditSpecification#visibleToRequester: officeCode IN
                        // (...) OR a shipment whose parties sit in one of those offices), so
                        // sending only orgData[0] hid every transaction belonging to the
                        // user's other offices. Matches the shared convention — see
                        // tableSavedViewsApi and the Contracts APIs.
                        "Office-Code": Cookies.get("officeCodes") || Cookies.get("officeCode"),
                    },
                }
            );
            setData(res.data.content || []);
            setTotalElements(res.data.totalElements || 0);
            setTotalPages(res.data.totalPages || 1);
        } catch {
            setError("Failed to load transaction audit data.");
        } finally {
            setLoading(false);
        }
    }, [currentPage, itemsPerPage, sortBy, ascending, debouncedKeyword,
        fileType, flowType, referenceType, processingStatus, source]);

    // Wait for saved views: the active view owns the initial sort / filters / page size.
    useEffect(() => {
        if (viewsLoading) return;
        fetchData();
    }, [fetchData, viewsLoading]);

    // The top-bar search box opens results in a new tab, like Orders/Claims/etc.,
    // leaving this tab's own filters untouched. The dropdown's keyword field (part
    // of "Apply Filters") is a separate, in-place filter — see handleApplyFilters.
    const handleSearch  = () => {
        if (!searchQuery.trim()) return;
        onSearchNewTab?.(searchQuery.trim());
    };
    const handleReload  = () => { setReloading(true); fetchData().finally(() => setReloading(false)); };

    const handleSort = (nextSortBy) => {
        setCurrentPage(1);
        if (sortBy !== nextSortBy) { setSortBy(nextSortBy); setAscending(true); }
        else setAscending((p) => !p);
        markDirty();
    };

    /**
     * Applies the dropdown's staged selections; `null` resets everything
     * (the dropdown's Reset button), mirroring ManageTeamDetails.
     */
    const handleApplyFilters = (draft) => {
        if (!draft) {
            applyFilterValues(DEFAULT_FILTER_VALUES);
            applyKeyword("");
            setCurrentPage(1);
            markDirty();
            return;
        }

        applyFilterValues(toApiFilterValues(draft));
        applyKeyword(draft.keyword ?? "");
        setCurrentPage(1);
        markDirty();
    };

    // ── Action dropdown (portal-based to escape stacking contexts) ──
    const handleActionClick = (id, e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + window.scrollY, left: rect.left + window.scrollX });
        setOpenMenu((prev) => (prev === id ? null : id));
    };

    const renderActionColumn = (rowId, getStickyCellProps) => {
        const isOpen = openMenu === rowId;
        const { className: stickyClass, style: stickyStyle } = getStickyCellProps("Action");

        return (
            <>
                <td
                    onClick={(e) => e.stopPropagation()}
                    className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center transition-none z-[10] ${stickyClass}`}
                    style={{
                        ...stickyStyle,
                        width: `${ACTION_COLUMN_WIDTH}px`, minWidth: `${ACTION_COLUMN_WIDTH}px`, maxWidth: `${ACTION_COLUMN_WIDTH}px`,
                    }}
                >
                    <button
                        data-action-trigger
                        className="p-1 relative w-full transition-none text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-acs-dark-text"
                        onClick={(e) => handleActionClick(rowId, e)}
                    >
                        <MoreHorizontal size={18} className="mx-auto" />
                    </button>
                </td>

                {isOpen && ReactDOM.createPortal(
                    <div
                        ref={dropdownRef}
                        style={{ position: "absolute", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}
                    >
                        <ActionMenu
                            rowId={rowId}
                            isOpen
                            onClose={() => setOpenMenu(null)}
                            moduleName="Transaction"
                            actions={[
                                { icon: "Eye", label: "View", color: "blue", action: () => setOpenMenu(null) },
                            ]}
                        />
                    </div>,
                    document.body
                )}
            </>
        );
    };

    // Fetches a short-lived presigned URL for one row's document and opens it in a
    // new tab, rather than embedding the raw S3 key/URL in the list response.
    const handleOpenDocument = useCallback(async (id) => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/transaction-audit/${id}/document-url`,
                {
                    headers: {
                        Authorization: `Bearer ${Cookies.get("authToken")}`,
                        "Office-Code": Cookies.get("officeCodes") || Cookies.get("officeCode"),
                    },
                }
            );
            if (res.data) {
                window.open(res.data, "_blank", "noopener,noreferrer");
            }
        } catch (err) {
            notify.error({
                title: "Unable to open document",
                message: err?.response?.data?.message || "Could not load the document. Please try again.",
            });
        }
    }, []);

    // ── Row formatting ──
    const formattedTransactions = React.useMemo(
        () => data.map((t) => formatTransactionRow(t, handleOpenDocument)),
        [data, handleOpenDocument]
    );

    const savedViewsControl = (
        <TableSavedViewsControl
            controlLabel={controlLabel}
            views={views}
            activeView={activeView}
            loading={viewsLoading}
            error={viewsError}
            isDirty={isDirty}
            allDataHeaders={TABLE_HEADERS.slice(2)}
            dataHeaders={dataHeaders}
            onSelectView={selectView}
            onSaveNew={saveNewView}
            onUpdateActive={updateActiveView}
            onRename={renameView}
            onDelete={deleteView}
            onSetDefault={setDefaultView}
            onUpdateColumnVisibility={updateColumnVisibility}
            onManageColumns={onManageColumns}
        />
    );

    const transactionsTab = (
      <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full transition-none">
        <div className="flex items-center justify-end gap-2 border-b border-gray-200 px-3 py-2 dark:border-acs-dark-border">
          {savedViewsControl}
        </div>

        {error ? (
          <div className="flex-1 flex items-center justify-center text-fluid-sm font-roboto text-red-500 dark:text-red-400">
            {error}
          </div>
        ) : (
          <div
            ref={tableScrollRef}
            className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto bg-white dark:bg-acs-dark-surface transition-none relative"
          >
            <Table
              {...columnManagerProps}
              data={formattedTransactions}
              initialColumnWidths={savedColumnWidths}
              onColumnResize={onColumnResize}
              selectedRows={[]}
              totalValidRows={formattedTransactions.length}
              onSelectAll={() => {}}
              renderCheckboxActions={() => {}}
              checkboxHeaderRef={checkboxHeaderRef}
              autoGrowColumns
              dataColumnMinWidth={DATA_COLUMN_MIN_WIDTH}
              dataColumnMaxWidth={DATA_COLUMN_MAX_WIDTH}
              enableColumnResize
              enableColumnReorder
              enableColumnPin
              nonHideableColumnIds={[TRANSACTION_NUMBER_HEADER]}
              sortBy={sortBy}
              ascending={ascending}
              onSort={(h) => { const k = SORTABLE_COLUMNS[h]; if (k) handleSort(k); }}
              sortableHeaders={Object.keys(SORTABLE_COLUMNS)}
              sortKeyByHeader={(h) => SORTABLE_COLUMNS[h]}
            >
              {(row, _rowIdx, columnWidths, getStickyCellProps, visibleColumns) => {
                const rowId = row._id;
                const { className: checkboxClass, style: checkboxStyle } = getStickyCellProps("Selection");
                const orderedDataHeaders = (visibleColumns ?? [])
                  .map((col) => col.id)
                  .filter((id) => id !== "Selection" && id !== "Action");
                const dataHeadersToRender = orderedDataHeaders.length ? orderedDataHeaders : dataHeaders;

                return (
                  <>
                    {/* Selection */}
                    <td
                      className={`bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border z-[10] transition-none ${checkboxClass}`}
                      style={{
                        ...checkboxStyle,
                        width: `${SELECTION_COLUMN_WIDTH}px`, minWidth: `${SELECTION_COLUMN_WIDTH}px`, maxWidth: `${SELECTION_COLUMN_WIDTH}px`,
                      }}
                    >
                      <input
                        type="checkbox"
                        className="relative appearance-none cursor-pointer mx-auto block w-[15px] h-[15px] rounded-sm border border-gray-400 dark:border-acs-dark-border bg-white dark:bg-acs-dark-surface transition-none checked:bg-acs-checkbox dark:checked:bg-acs-checkbox-dark checked:border-acs-checkbox after:content-[''] after:absolute after:inset-0 after:flex after:items-center after:justify-center checked:after:content-['✓'] checked:after:text-white checked:after:text-[12px] checked:after:font-bold"
                        readOnly
                      />
                    </td>

                    {/* Action (portal dropdown) */}
                    {renderActionColumn(rowId, getStickyCellProps)}

                    {/* Data columns */}
                    {dataHeadersToRender.map((header) => {
                      const isExpanded = expandedRowKey === rowId;
                      const { className: colClass, style: colStyle } = getStickyCellProps(header);

                      return (
                        <td
                          key={header}
                          className={`px-2 sm:px-3 py-1 sm:py-2 text-gray-900 dark:text-acs-dark-text text-xs sm:text-sm md:text-base border-b border-gray-200 dark:border-acs-dark-border font-roboto transition-none ${colClass}`}
                          {...getRowHoverProps(rowId)}
                          style={{
                            ...colStyle,
                            whiteSpace:   isExpanded ? "normal" : "nowrap",
                            overflow:     "hidden",
                            textOverflow: "ellipsis",
                            // Lets unbroken tokens (file names, transaction ids) wrap too, not just space-separated text.
                            overflowWrap: "break-word",
                            wordBreak:    "break-word",
                            minWidth:     `${columnWidths?.[header] || DATA_COLUMN_MIN_WIDTH}px`,
                            maxWidth:     isExpanded ? `${DATA_COLUMN_MAX_WIDTH * 2}px` : `${DATA_COLUMN_MAX_WIDTH}px`,
                            width:        columnWidths?.[header] ? `${columnWidths[header]}px` : `${DATA_COLUMN_MIN_WIDTH}px`,
                            transition:   "max-width 120ms ease",
                          }}
                        >
                          {row[header]}
                        </td>
                      );
                    })}
                  </>
                );
              }}
            </Table>

            {/* Loading overlay */}
            {showLoadingOverlay && (
              <div className="absolute inset-0 z-[2000] flex items-center justify-center bg-white/60 dark:bg-black/30">
                <Loader color="text-blue-600 dark:text-acs-dark-text" message="Loading Transactions..." />
              </div>
            )}
          </div>
        )}

        <GlobalPagination
          label="Transactions per page"
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={setItemsPerPage}
        />
      </div>
    );

    // The top action bar portals into a slot the TransactionAudit shell renders
    // above the tab strip (in the purple header, matching Shipment/Orders — see
    // toolbarSlot below) instead of rendering inline here. Portaling keeps every
    // filter/search/count/reload as this tab's own state (so each search tab still
    // gets its own independent filters, and "Filter preferences saved to view"
    // keeps working per tab) while only relocating where it's drawn.
    const toolbar = (
      <div className="flex flex-wrap justify-between items-center rounded-md ml-[2] p-fluid-1">
        <div className="flex flex-wrap items-center gap-2">
          <FilterButton
            text="Filter by"
            contentComponent={
              <TransactionDropdownContent
                draft={draftFilters}
                setDraft={setDraftFilters}
                options={FILTER_OPTIONS}
                onApply={handleApplyFilters}
              />
            }
          />

          <SearchBox
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={handleSearch}
            placeholder="Search transaction no., reference..."
          />

          <span className="text-gray-500 text-fluid-sm font-semibold text-acs-light-text dark:text-acs-dark-text">
            {totalElements} Transactions
          </span>
          <button
            onClick={handleReload}
            className="p-1 rounded-full hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-[#223688]/30 transition"
            title="Reload"
            aria-label="Reload"
          >
            <RefreshCw
              size={16}
              className={`text-gray-500 text-acs-light-text dark:text-acs-dark-text ${reloading ? "animate-spin" : "hover:text-gray-700"}`}
            />
          </button>
        </div>
      </div>
    );

    return (
      <div className="flex flex-col font-roboto flex-1 h-full min-h-0 overflow-hidden">
        {toolbarSlot ? ReactDOM.createPortal(toolbar, toolbarSlot) : toolbar}

        <div className="flex-1 min-h-0 flex flex-col scrollbar-hide">
          {transactionsTab}
        </div>
      </div>
    );
};

// ─── Module shell ─────────────────────────────────────────────────────────────

/**
 * Chrome for /dashboard/transaction-audit — matches Yard Management / Shipment
 * module chrome (lavender background, "Transaction Audit Overview" title row,
 * collapsible accordion, then the audit table).
 */
const TransactionAudit = () => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  // The active tab's own toolbar (Filter by / Search / count / Reload) portals into
  // this DOM node, so it renders inside the purple header above the tab strip —
  // matching Shipment/Orders — instead of inside the white tab-content pane. A
  // callback ref, not useRef, because we need a re-render once it's attached so the
  // prop passed down below actually carries the node instead of null.
  const [toolbarSlot, setToolbarSlot] = useState(null);
  const dispatch = useDispatch();
  const { tabs, activeTab, defaultTabsCount } = useSelector(selectModuleTabs("transactionAudit"));

  // Opens a new closable "Search: …" tab instead of filtering the current one in
  // place — matches Orders/Claims/etc.
  const handleSearchNewTab = (query) => {
    dispatch(addTab({
      id: `search-${Date.now()}`,
      name: `Search: ${query}`,
      contentType: "search",
      searchQuery: query,
    }));
  };

  const handleRemoveTab = (index) => {
    if (index < defaultTabsCount) return;
    dispatch(removeTab({ tabIndex: index }));
  };

  return (
    <div className="flex h-full min-h-0 max-w-full w-full flex-1 flex-col gap-0 bg-custom-bg-purple">
      <div
        className="mt-fluid-2 flex w-fit cursor-pointer select-none items-center gap-1 rounded-xl pl-fluid-2"
        onClick={() => setIsCollapsed((prev) => !prev)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsCollapsed((prev) => !prev);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={!isCollapsed}
      >
        <h4 className="text-fluid-lg font-semibold text-acs-light-text transition-none dark:text-acs-dark-text">
          Transaction Audit Overview
        </h4>
        {isCollapsed ? (
          <ChevronRight
            className="h-[18px] w-[18px] text-[#223688] transition-none dark:text-[#6DE1C2]"
            aria-hidden
          />
        ) : (
          <ChevronDown
            className="h-[18px] w-[18px] text-[#223688] transition-none dark:text-[#6DE1C2]"
            aria-hidden
          />
        )}
      </div>

      <div className="fluid-0.5 flex min-h-0 flex-1 flex-col overflow-hidden">
        <div
          className={`overflow-hidden transition-none ease-in-out ${
            isCollapsed ? "max-h-0 opacity-0" : "max-h-[500px] opacity-100"
          }`}
        >
          {/* Reserved for future transaction summary cards (see ShipmentList / OrderList) */}
        </div>
        <div ref={setToolbarSlot} />
        <div className="min-h-0 flex-1 overflow-auto">
          <Tabs
            tabs={tabs.map((tab) => tab.name)}
            tabContents={tabs.map((tab) => (
              <TransactionAuditDetails
                key={tab.id}
                initialKeyword={tab.searchQuery || ""}
                onSearchNewTab={handleSearchNewTab}
                // Only a dynamic search tab gets its own pageKey — the default
                // Transactions tab keeps the shared "transaction-audit" one so the
                // user's real saved views still apply to it.
                stateKey={tab.contentType === "search" ? tab.id : undefined}
                toolbarSlot={toolbarSlot}
              />
            ))}
            activeTab={activeTab}
            onTabChange={(index) => dispatch(setActiveTab({ index }))}
            isDeletable={tabs.map((_, index) => index >= defaultTabsCount)}
            onRemoveTab={handleRemoveTab}
          />
        </div>
      </div>
    </div>
  );

};

export default TransactionAudit;